import { ShipApi } from "../../Types/Generated/LogbookApi/ShipApi"
import { entity } from "./Connection"

function delegatedUserId(ids: object): number | undefined {
  const value = Reflect.get(ids, "userId");
  return typeof value === "number" ? value : undefined;
}

// #1792 (tracker #1652): `ship_users.id` is unique per ship, not per account, so
// one user's cross-ship membership list routinely carries several rows sharing an
// id (confirmed against a real run database: id 7 existed on both ship 6 and ship
// 7). The store keys entities by type + id, so such rows cannot all be represented
// -- writing them collapses the set to whichever landed last, and because the list
// also claims `{ userId }` authority it *replaces* the selected ship's own roster
// with that collapsed set. The selected ship is then left with no roster row for
// the logged-in user, which `AppView`'s access-state effect reads as "no access"
// and evicts the user out of a ship they fully own (seen as
// `companion-connect-sync.spec.ts` bouncing back to `/app/account`).
//
// `DataStore.isCrossShipShipUserCollision` already refuses the same overwrite for
// live broadcasts; this is the fetch-side half of that identity boundary. Only the
// ambiguous rows are withheld, so a list whose ids happen not to collide keeps the
// exact entity/authority behaviour #1642 (tracker #665) designed. Every row --
// withheld or not -- still reaches the ship picker through `extras.membership`,
// which `mergeLiveMembership` matches on shipId as well as id.
function membershipWithoutIdCollisions<T extends { id: number }>(rows: readonly T[]): T[] {
  const collidingIds = new Set<number>();
  const seen = new Map<number, T>();
  for (const row of rows) {
    const previous = seen.get(row.id);
    if (previous !== undefined && previous !== row) {
      collidingIds.add(row.id);
    }
    seen.set(row.id, row);
  }
  return rows.filter((row) => !collidingIds.has(row.id));
}

// Ship provides both ship acquisitions. A shipId fetch owns the selected ship's
// complete crew/settings bundle; a userId fetch owns the all-ships list and only
// that user's cross-ship membership rows. ShipUser delegates here so both scopes
// keep one request and one normalized store without conflating their authority.
export const ShipEntity = entity("Ship", {
  fetch: (client, ids) => {
    const shipId = ids.shipId;
    if (shipId !== undefined) {
      return new ShipApi(client).ships.get({ params: { shipId } }).call().then((r) => {
        const shipUsers = [...(r.users ?? [])];
        if (r.user) {
          const activeIndex = shipUsers.findIndex((user) => user.id === r.user?.id);
          if (activeIndex === -1) {
            shipUsers.push(r.user);
          } else {
            const hydrated = shipUsers[activeIndex];
            shipUsers[activeIndex] = {
              ...r.user,
              name: r.user.name || hydrated.name,
              shortname: r.user.shortname || hydrated.shortname,
            };
          }
        }
        return {
          entities: {
            Ship: r.ship ? [r.ship] : [],
            ShipUser: shipUsers,
            Settings: r.settings ?? [],
          },
          extras: {
            activeShipUserId: r.user?.id,
            settings: r.settings ?? [],
            ship: r.ship,
            shipUsers,
          },
          authoritative: {
            ShipUser: { shipId },
            Settings: { shipId },
          },
        };
      });
    }

    const userId = delegatedUserId(ids);
    if (userId !== undefined) {
      return new ShipApi(client).ships.list({ params: {} }).call().then((r) => {
        const membership = r.users ?? [];
        const storable = membershipWithoutIdCollisions(membership);
        const complete = storable.length === membership.length;
        return {
          entities: {
            Ship: r.ships ?? [],
            ShipUser: storable,
          },
          // ShipUser ids are only unique inside their account/ship scope. The
          // all-ships response can therefore contain repeated ids, which a
          // type-global entity map cannot preserve. Keep the exact membership
          // rows with the provider query for the ship picker.
          extras: { ownShipId: r.ownShipId, membership },
          partial: ["ShipUser"],
          // Authority means "this response is the complete set for this scope".
          // Once a colliding row has been withheld that is no longer true, so
          // claiming it would evict the very rows this response cannot carry --
          // including the selected ship's own roster. Drop the claim instead of
          // asserting something the response can't back up.
          authoritative: complete
            ? { Ship: {}, ShipUser: { userId } }
            : { Ship: {} },
        };
      });
    }

    return Promise.reject(new Error("Ship fetch requires shipId or userId"));
  },
});
